const KEY = 'puzzlemania_settings';
/**
 * What a player who has never opened Settings gets.
 *
 * ## Volumes: 60%, not 100%
 *
 * Both games open with audio ON — muting a new arrival hides the work entirely
 * — but full scale is the wrong opening bid for a browser/portal game that a
 * player may well have started in a background tab. 60% is audible without
 * being the loudest thing on the machine, and leaves the slider real headroom
 * in BOTH directions, which a 100% default does not.
 *
 * This is an INITIAL value only. `load()` overlays whatever is stored on top,
 * so a player who has ever touched either slider keeps their number: changing
 * this line cannot move an existing player's volume. The distinction is the
 * same one {@link SettingsSaveModule.hasStored} exists for on `language`.
 *
 * ## `language: 'en'`
 *
 * Kept as the last-resort answer, and deliberately NOT where the default
 * language is decided any more — that is `initialLanguage` in `initial-locale`,
 * which detects the player's own locale and only lands here when it cannot.
 */
const DEFAULTS = { musicVolume: 0.6, sfxVolume: 0.6, language: 'en' };
export const SettingsSaveModule = {
    load() {
        try {
            const raw = localStorage.getItem(KEY);
            return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
        }
        catch {
            return { ...DEFAULTS };
        }
    },
    /**
     * Module 41 (WP-41.7) — only what the player has actually stored, with no
     * defaults merged in.
     *
     * `load()` cannot answer "has the player ever chosen a language?", because it
     * folds the `en` default into the answer, so a first-time visitor and someone
     * who deliberately picked English are indistinguishable. That distinction is
     * the whole precedence rule for SDK locale detection: detection supplies the
     * *initial* value only, and must never overwrite a real choice.
     */
    loadStored() {
        try {
            const raw = localStorage.getItem(KEY);
            return raw ? JSON.parse(raw) : {};
        }
        catch {
            return {};
        }
    },
    /** Whether the player has explicitly stored a value for `field`. */
    hasStored(field) {
        return SettingsSaveModule.loadStored()[field] !== undefined;
    },
    /**
     * Merges over what is **stored**, not over `load()`'s defaulted view.
     *
     * Module 41 (WP-41.7) fixed this: merging over `load()` wrote all three fields
     * every time, so changing the volume silently persisted `language: 'en'` too —
     * recording a language "choice" the player never made. Nothing noticed while
     * storage was the only source of the language, but it breaks any rule that
     * asks whether the player has actually decided something, and on CrazyGames it
     * meant muting the game pinned the UI to English for good.
     *
     * `load()` is unaffected: it still merges `DEFAULTS`, so every reader sees a
     * complete `SettingsData` exactly as before.
     */
    save(patch) {
        const stored = SettingsSaveModule.loadStored();
        localStorage.setItem(KEY, JSON.stringify({ ...stored, ...patch }));
    },
};
